import { useState } from 'react'
import { Pencil, Check, X, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { actionLabel, deriveAction } from './actionLabel'
import {
  removeActionOverride, saveActionOverride,
  type ActionRow,
} from '../../hooks/useAnalytics'

/**
 * What people did — and where they get named.
 *
 * Naming is inline rather than on a settings page on purpose: the moment you
 * know what an endpoint is, is the moment you are looking at it in a list
 * wondering what it is. A separate screen would collect names nobody writes.
 */

export interface ActionTableProps {
  rows: ActionRow[]
  loading?: boolean
  /** Show the raw method and route under each label. Technical view only. */
  showRoutes?: boolean
  /**
   * Show the request count beside the collapsed one. Technical only: "57 /115"
   * reads as a score to anyone who does not already know what was collapsed.
   */
  showRequests?: boolean
  /** Editing is a write against the shared dictionary — never silent. */
  editable?: boolean
  onSaved?: () => void
  emptyMessage?: string
}

const ACTIVITY_CHOICES = [
  { value: '', label: 'Classify automatically' },
  { value: 'view', label: 'A screen someone opened' },
  { value: 'action', label: 'Something someone did' },
  { value: 'background', label: 'Polling / plumbing' },
]

function relativeTime(ts: number | null | undefined): string {
  if (!ts) return '—'
  const mins = Math.floor((Date.now() - Number(ts)) / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return days < 30 ? `${days}d ago` : `${(days / 30.44).toFixed(1)}mo ago`
}

function RowEditor({ row, columns, onDone }: { row: ActionRow; columns: number; onDone: () => void }) {
  const derived = deriveAction(row.method, row.pathTemplate)
  const [label, setLabel] = useState(row.label ?? derived.label)
  const [category, setCategory] = useState(row.category ?? '')
  // Only an EXPLICIT override preselects. Seeding this from the effective
  // classification would turn "the classifier decided this" into "a person
  // decided this" the first time anyone edited the label.
  const [activity, setActivity] = useState(row.activityOverride ?? '')
  const [hidden, setHidden] = useState(row.hidden)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const key = { seed: row.seed, method: row.method, pathTemplate: row.pathTemplate }

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await saveActionOverride({ ...key, label, category: category || null, activity: activity || null, hidden })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save')
      setSaving(false)
    }
  }

  const reset = async () => {
    setSaving(true)
    setError(null)
    try {
      await removeActionOverride(key)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset')
      setSaving(false)
    }
  }

  return (
    <TableRow>
      <TableCell colSpan={columns} className="bg-muted/40 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              What this is, in plain words
            </span>
            <Input
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder={derived.label}
              className="h-9 w-[22rem]"
              autoFocus
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Group (optional)
            </span>
            <Input
              value={category}
              onChange={e => setCategory(e.target.value)}
              placeholder="Verification"
              className="h-9 w-[12rem]"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Counts as
            </span>
            <select
              value={activity}
              onChange={e => setActivity(e.target.value)}
              className="h-9 w-[16rem] rounded-md border border-input bg-background px-2 text-sm"
            >
              {ACTIVITY_CHOICES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>

          <label className="flex h-9 items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} />
            Hide from the activity view
          </label>

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={saving || !label.trim()}>
              <Check className="mr-1 size-3.5" /> Save
            </Button>
            <Button size="sm" variant="ghost" onClick={onDone} disabled={saving}>
              <X className="mr-1 size-3.5" /> Cancel
            </Button>
            {row.named && (
              <Button size="sm" variant="ghost" onClick={reset} disabled={saving}>
                Reset to automatic
              </Button>
            )}
          </div>
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          Applies to every event on this route, past and future, in every app —
          <code className="mx-1 font-mono">{row.method} {row.pathTemplate}</code>
          has {row.events.toLocaleString()} recorded{row.app ? ` in ${row.app}` : ''}.
        </p>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </TableCell>
    </TableRow>
  )
}

export function ActionTable({
  rows,
  loading = false,
  showRoutes = false,
  showRequests = false,
  editable = false,
  onSaved,
  emptyMessage = 'No activity in this range',
}: ActionTableProps) {
  const [editing, setEditing] = useState<string | null>(null)
  const keyOf = (r: ActionRow) => `${r.seed}|${r.app ?? ''}|${r.method}|${r.pathTemplate}`
  const columns = showRequests ? 9 : 8

  if (loading && rows.length === 0) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
  }
  if (rows.length === 0) {
    return <div className="p-8 text-center text-sm text-muted-foreground">{emptyMessage}</div>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Seed</TableHead>
          <TableHead>App</TableHead>
          <TableHead>What</TableHead>
          <TableHead>Group</TableHead>
          <TableHead className="text-right">Times</TableHead>
          {showRequests && (
            <TableHead className="text-right" title="Every request received, before repeats within a minute were collapsed">
              Requests
            </TableHead>
          )}
          <TableHead className="text-right">People</TableHead>
          <TableHead className="text-right">Last</TableHead>
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(row => {
          const rowKey = keyOf(row)
          if (editing === rowKey) {
            return (
              <RowEditor
                key={rowKey}
                row={row}
                columns={columns}
                onDone={() => { setEditing(null); onSaved?.() }}
              />
            )
          }
          return (
            <TableRow key={rowKey}>
              <TableCell className="whitespace-nowrap text-muted-foreground">{row.seed || '—'}</TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">{row.app ?? '—'}</TableCell>
              <TableCell className="max-w-[28rem]">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-foreground">
                    {actionLabel(row.method, row.pathTemplate, row.label)}
                  </span>
                  {!row.named && (
                    // Flagged, not hidden: a guessed name that looks authored
                    // is worse than one that admits it.
                    <Badge variant="secondary" className="shrink-0 text-[10px]">auto</Badge>
                  )}
                  {row.hidden && <EyeOff className="size-3.5 shrink-0 text-muted-foreground" />}
                </div>
                {showRoutes && (
                  <div className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                    {row.method} {row.pathTemplate}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">{row.category ?? '—'}</TableCell>
              <TableCell className="text-right tabular-nums">{row.activity.toLocaleString()}</TableCell>
              {showRequests && (
                // `events`, not `rawInteractions`: with plumbing listed, a poll
                // has requests but no interactions at all.
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {row.events.toLocaleString()}
                </TableCell>
              )}
              <TableCell className="text-right tabular-nums text-muted-foreground">
                {row.uniqueUsers.toLocaleString()}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right text-muted-foreground">
                {relativeTime(row.lastSeen)}
              </TableCell>
              <TableCell>
                {editable && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    aria-label={`Name ${row.method} ${row.pathTemplate}`}
                    onClick={() => setEditing(rowKey)}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default ActionTable
